import type { CSSProperties } from 'react';
import { formatCost } from '../../../../helpers/utils';

/** Shared black header cell (FitCrunch line-items table). */
const FITCRUNCH_TH_BLACK_BASE: CSSProperties = {
    borderBottom: '1px solid #000',
    padding: '4px',
    backgroundColor: '#000',
    color: '#fff',
    fontSize: '12px',
};

/** Single source of truth for FitCrunch line-items header (print slip + DOM row measurement). */
export const FITCRUNCH_LINE_ITEMS_THEAD_COLUMNS: readonly { label: string; thStyle: CSSProperties }[] = [
    {
        label: 'Item Title',
        thStyle: { ...FITCRUNCH_TH_BLACK_BASE, textAlign: 'left', width: '50%' },
    },
    {
        label: 'Qty',
        thStyle: { ...FITCRUNCH_TH_BLACK_BASE, textAlign: 'right', whiteSpace: 'nowrap' },
    },
    {
        label: 'Unit Price',
        thStyle: { ...FITCRUNCH_TH_BLACK_BASE, textAlign: 'right' },
    },
    {
        label: 'Tax Paid',
        thStyle: { ...FITCRUNCH_TH_BLACK_BASE, textAlign: 'right' },
    },
    {
        label: 'Final Cost',
        thStyle: { ...FITCRUNCH_TH_BLACK_BASE, textAlign: 'right' },
    },
];

function cssPropertiesToInlineAttr(style: CSSProperties): string {
    return (Object.entries(style) as [string, string | number][])
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(([camel, val]) => {
            const kebab = camel.replaceAll(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
            const value = typeof val === 'number' ? `${val}px` : String(val);
            return `${kebab}:${value}`;
        })
        .join(';');
}

function escapeHtmlForAttr(s: string): string {
    return s
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;');
}

/** `<thead>…</thead>` string for `measureFitCrunchOrderItemRowHeights` (must match `FitCrunchLineItemsThead`). */
export function buildFitCrunchLineItemsTheadHtml(): string {
    const cells = FITCRUNCH_LINE_ITEMS_THEAD_COLUMNS.map(
        ({ label, thStyle }) =>
            `<th style="${cssPropertiesToInlineAttr(thStyle)}">${escapeHtmlForAttr(label)}</th>`,
    ).join('');
    return `<thead><tr>${cells}</tr></thead>`;
}

/** FitCrunch line-items header row (Item Title … Final Cost). */
export function FitCrunchLineItemsThead(): JSX.Element {
    return (
        <thead>
            <tr>
                {FITCRUNCH_LINE_ITEMS_THEAD_COLUMNS.map(({ label, thStyle }) => (
                    <th key={label} style={thStyle}>
                        {label}
                    </th>
                ))}
            </tr>
        </thead>
    );
}

/** Inner `packing-slip-line-table` with header only (measurement chrome). */
export function FitCrunchLineItemsTableChromeEmpty(): JSX.Element {
    return (
        <div style={{ width: '100%' }}>
            <div style={{ margin: '8px 0' }}>
                <table className="packing-slip-line-table" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '2px' }}>
                    <FitCrunchLineItemsThead />
                    <tbody />
                </table>
            </div>
        </div>
    );
}

interface FitCrunchOrderTotalsBlockProps {
    orderDetails: any;
}

/** SubTotal / Tax / Shipping / Total block (chrome-footer + continuation totals). */
export function FitCrunchOrderTotalsBlock({ orderDetails }: FitCrunchOrderTotalsBlockProps): JSX.Element {
    return (
        <div style={{ margin: '4px 0', textAlign: 'right' }}>
            {orderDetails?.sales_channel_id && (
                <p style={{ margin: '0', fontSize: '14px', textAlign: 'left' }}>{orderDetails?.salesChannel?.name}</p>
            )}
            <table style={{ width: 'auto', borderCollapse: 'collapse', marginLeft: 'auto' }} aria-hidden={true}>
                <tbody>
                    <tr>
                        <td style={{ padding: '1px 4px', textAlign: 'right', fontSize: '12px' }}>
                            <strong>SubTotal:</strong>
                        </td>
                        <td style={{ padding: '1px 4px', textAlign: 'right', fontSize: '12px' }}>
                            ${formatCost(orderDetails?.order_total)}
                        </td>
                    </tr>
                    <tr>
                        <td style={{ padding: '1px 4px', textAlign: 'right', fontSize: '12px' }}>
                            <strong>Tax:</strong>
                        </td>
                        <td style={{ padding: '1px 4px', textAlign: 'right', fontSize: '12px' }}>{formatCost('$0.00')}</td>
                    </tr>
                    <tr>
                        <td style={{ padding: '1px 4px', textAlign: 'right', fontSize: '12px' }}>
                            <strong>Shipping Cost:</strong>
                        </td>
                        <td style={{ padding: '1px 4px', textAlign: 'right', fontSize: '12px' }}>{formatCost('$0.00')}</td>
                    </tr>
                    <tr>
                        <td style={{ padding: '4px', textAlign: 'right', fontSize: '12px' }}>
                            <strong>Total:</strong>
                        </td>
                        <td style={{ padding: '4px', textAlign: 'right', fontSize: '12px' }}>
                            ${formatCost(orderDetails?.order_total)}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
}
